/**
 * DimensionsOverlay — R3F component that renders vehicle dimension annotations.
 *
 * Renders 3D measurement lines (L × W × H) with text labels using
 * `@react-three/drei` Line and Text components. Only visible when the
 * DimensionsManager reports `visible === true` and `enabled === true`.
 *
 * Two flat SVG icon buttons on the ground toggle the overlay:
 * - Ruler icon (icon-measures-on.svg) when lines are hidden
 * - X icon (icon-measures-off.svg) when lines are visible
 *
 * @see DimensionsManager.ts
 * @see docs/ims-engine-api/12-feature-pano-measures.md
 */

import { Suspense, useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useLoader, useThree } from '@react-three/fiber';
import { Line, Text } from '@react-three/drei';
import { TextureLoader, SRGBColorSpace } from 'three';
import type { DimensionsManager } from './DimensionsManager';
import type { DimensionsValues, DimensionsAABB } from '../api/types/dimensions';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface DimensionsOverlayProps {
  /** The DimensionsManager instance (source of truth). */
  manager: DimensionsManager;
}

// ---------------------------------------------------------------------------
// Constants — values matched from legacy CarMeasures component
// ---------------------------------------------------------------------------

/** Offset from model AABB edges for line + icon positioning. */
const LINE_OFFSET = 0.3;
/** Font size for dimension labels. */
const FONT_SIZE = 0.08;
/** Line colour. */
const LINE_COLOR = 'white';
/** Line width. */
const LINE_WIDTH = 1;
/** Label offset from ground (Y-direction). */
const LABEL_OFFSET = 0.15;
/** Thin outline for elegant text styling (new improvement). */
const OUTLINE_WIDTH = 0.003;
/** Icon size (square plane width/height). */
const ICON_SIZE = 0.2;

/**
 * Inline SVG data URIs — bundled with the engine, no external files needed.
 * Transforms are pre-computed from the original nested <g> + element transforms.
 * ON:  group translate(22591,-9145) + path translate(-21693.049,8043.073) = translate(897.951,-1101.927)
 * OFF: group translate(1609,-5661)  + path translate(-1568.416,5701.584)  = translate(40.584,40.584)
 */
const ICON_ON_URI = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="154" height="154" viewBox="0 0 154 154"><circle cx="77" cy="77" r="77" opacity="0.4"/><path d="M-871,1150.97l77.888,78.084,22.209-22.17-77.888-78.084Zm95.405,55.914-14.194,14.193-73.235-73.392,14.233-14.154,9.932,9.932-4.379,4.379,2.346,2.346,4.379-4.379,4.692,4.692-6.725,6.725,2.346,2.346,6.725-6.725,4.692,4.692-4.379,4.379,2.346,2.346,4.379-4.379,4.692,4.692-6.725,6.725,2.346,2.346,6.725-6.725,4.692,4.692-4.379,4.379,2.346,2.346,4.379-4.379,4.692,4.692-6.725,6.725,2.346,2.346,6.725-6.725,4.692,4.692-4.379,4.379,2.346,2.346,4.379-4.379,4.692,4.692-6.725,6.725,2.346,2.346,6.725-6.725,4.692,4.692-4.379,4.379,2.346,2.346,4.379-4.379Zm-75.581-63.851a3.127,3.127,0,0,1,.078,4.379,3.127,3.127,0,0,1-4.379.078,3.127,3.127,0,0,1-.078-4.379A3.127,3.127,0,0,1-851.176,1143.032Z" transform="translate(897.951 -1101.927)" fill="#fff"/></svg>')}`;
const ICON_OFF_URI = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="154" height="154" viewBox="0 0 154 154"><circle cx="77" cy="77" r="77" opacity="0.4"/><path d="M36.416,43.487,7.071,72.832,0,65.761,29.345,36.416,0,7.071,7.071,0,36.416,29.345,65.761,0l7.071,7.071L43.487,36.416,72.832,65.76l-7.07,7.071Z" transform="translate(40.584 40.584)" fill="#fff"/></svg>')}`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type Vec3Tuple = [number, number, number];

/** Format metres to display string: "4.51 m". */
function fmt(metres: number): string {
  return `${metres.toFixed(2)} m`;
}

// ---------------------------------------------------------------------------
// DimensionsOverlay
// ---------------------------------------------------------------------------

export function DimensionsOverlay({ manager }: DimensionsOverlayProps) {
  // React state — only updated when values actually change (avoids reconciliation)
  const [visible, setVisible] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [values, setValues] = useState<DimensionsValues>({ length: 0, width: 0, height: 0 });
  const [aabb, setAabb] = useState<DimensionsAABB | null>(null);
  const invalidate = useThree((s) => s.invalidate);

  // Track previous values in refs to avoid reference-equality false positives
  const prevVisibleRef = useRef(false);
  const prevEnabledRef = useRef(false);
  const prevValuesRef = useRef({ length: 0, width: 0, height: 0 });
  const prevAabbMinXRef = useRef<number | null>(null);

  // Subscribe to manager state changes instead of polling every frame.
  // DimensionsManager fires onChange only when state actually mutates,
  // so this triggers React re-renders only when needed (not 60×/s).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- intentional: wires manager callback to React state (R3F bridge pattern)
    manager.onChange = () => {
      const state = manager.getState();
      const currentAabb = manager.getAABB();

      let needsInvalidate = false;

      if (state.visible !== prevVisibleRef.current) {
        prevVisibleRef.current = state.visible;
        setVisible(state.visible);
        needsInvalidate = true;
      }
      if (state.enabled !== prevEnabledRef.current) {
        prevEnabledRef.current = state.enabled;
        setEnabled(state.enabled);
        needsInvalidate = true;
      }
      const pv = prevValuesRef.current;
      if (state.values.length !== pv.length || state.values.width !== pv.width || state.values.height !== pv.height) {
        prevValuesRef.current = state.values;
        setValues(state.values);
        needsInvalidate = true;
      }
      if (currentAabb && currentAabb.min.x !== prevAabbMinXRef.current) {
        prevAabbMinXRef.current = currentAabb.min.x;
        setAabb(currentAabb);
        needsInvalidate = true;
      }

      if (needsInvalidate) invalidate();
    };
    return () => {
      manager.onChange = undefined;
    };
  }, [manager, invalidate]);

  // Don't render anything if not initialised
  if (!aabb) return null;

  const { min, max } = aabb;

  return (
    <group>
      {/* ── Ground toggle icon — front-left corner ── */}
      {enabled && (
        <Suspense fallback={null}>
          <GroundToggleButton
            position={[min.x - 0.27, min.y + 0.03, max.z + 0.27]}
            linesVisible={visible}
          />
        </Suspense>
      )}

      {/* ── Dimension lines + labels ── */}
      {visible && <DimensionLines aabb={aabb} values={values} min={min} max={max} />}
    </group>
  );
}

// ---------------------------------------------------------------------------
// GroundToggleButton — SVG icon on the ground (Suspense-driven texture load)
// ---------------------------------------------------------------------------

interface GroundToggleButtonProps {
  position: Vec3Tuple;
  /** Whether the dimension lines are currently visible. */
  linesVisible: boolean;
}

function GroundToggleButton({ position, linesVisible }: GroundToggleButtonProps) {
  const [onTex, offTex] = useLoader(TextureLoader, [ICON_ON_URI, ICON_OFF_URI]);
  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- Three.js requires colorSpace to be set after texture load
    onTex.colorSpace = SRGBColorSpace;
    // eslint-disable-next-line react-hooks/immutability -- Three.js requires colorSpace to be set after texture load
    offTex.colorSpace = SRGBColorSpace;
  }, [onTex, offTex]);

  // Ruler icon when lines are hidden (→ click to show), X icon when shown (→ click to hide)
  const texture = linesVisible ? offTex : onTex;

  return (
    <mesh
      position={position}
      rotation={[-Math.PI / 2, 0, 0]}
      name="CarMeasuresIcon"
    >
      <planeGeometry args={[ICON_SIZE, ICON_SIZE]} />
      <meshBasicMaterial map={texture} transparent />
    </mesh>
  );
}

// ---------------------------------------------------------------------------
// DimensionLines — measurement lines + labels (positions from legacy script)
// ---------------------------------------------------------------------------

interface DimensionLinesProps {
  aabb: DimensionsAABB;
  values: DimensionsValues;
  min: DimensionsAABB['min'];
  max: DimensionsAABB['max'];
}

function DimensionLines({ values, min, max }: DimensionLinesProps) {
  const groundY = min.y + 0.02;
  const midX = (min.x + max.x) / 2;
  const midY = (min.y + max.y) / 2;
  const midZ = (min.z + max.z) / 2;

  return (
    <group>
      {/* ── Length (X-axis) — front edge, offset +Z ── */}
      <Line
        points={[
          [min.x, groundY, max.z + LINE_OFFSET],
          [max.x, groundY, max.z + LINE_OFFSET],
        ]}
        color={LINE_COLOR}
        lineWidth={LINE_WIDTH}
      />
      <Text
        position={[midX, groundY + LABEL_OFFSET, max.z + LINE_OFFSET]}
        fontSize={FONT_SIZE}
        color={LINE_COLOR}
        anchorX="center"
        anchorY="middle"
        outlineWidth={OUTLINE_WIDTH}
        outlineColor="#000000"
      >
        {fmt(values.length)}
      </Text>

      {/* ── Width (Z-axis) — left edge, offset −X ── */}
      <Line
        points={[
          [min.x - LINE_OFFSET, groundY, min.z],
          [min.x - LINE_OFFSET, groundY, max.z],
        ]}
        color={LINE_COLOR}
        lineWidth={LINE_WIDTH}
      />
      <Text
        position={[min.x - LINE_OFFSET, min.y + LABEL_OFFSET, midZ]}
        fontSize={FONT_SIZE}
        color={LINE_COLOR}
        anchorX="center"
        anchorY="middle"
        rotation={[-Math.PI / 2, -Math.PI / 2, -Math.PI / 2]}
        outlineWidth={OUTLINE_WIDTH}
        outlineColor="#000000"
      >
        {fmt(values.width)}
      </Text>

      {/* ── Height (Y-axis) — rear-left corner ── */}
      <Line
        points={[
          [min.x - LINE_OFFSET, min.y +0.02, min.z],
          [min.x - LINE_OFFSET, max.y, min.z],
        ]}
        color={LINE_COLOR}
        lineWidth={LINE_WIDTH}
      />
      <Text
        position={[min.x - LINE_OFFSET, midY, min.z - 0.1 - LABEL_OFFSET]}
        fontSize={FONT_SIZE}
        color={LINE_COLOR}
        anchorX="center"
        anchorY="middle"
        rotation={[-Math.PI / 1, -Math.PI / 2, -Math.PI / 1]}
        outlineWidth={OUTLINE_WIDTH}
        outlineColor="#000000"
      >
        {fmt(values.height)}
      </Text>
    </group>
  );
}
